import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { PlusCircle, Trash2, Save, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import AllocationTemplates, { ALLOCATION_TEMPLATES } from './AllocationTemplates';
import CustomAllocationManager from './CustomAllocationManager';
import { invalidateAccountBalances, beginSync, endSync } from '../hooks/useAccountBalances';
import { HelpSection, HelpP, HelpList, HelpRule, HelpCallout, HelpHeading } from './HelpSection';
import MoneyFlowExplainer from './MoneyFlowExplainer';

// Overage tolerance mirrors the backend guard in `create_or_update_financial_plan`.
// A tolerance of 0.005 accepts float noise (100.00001%) while rejecting 100.01%.
const ALLOCATION_TOLERANCE = 0.005;

// Monotonic id generator for row `_key` — used as a stable React key so edits
// and deletions in the middle of the list don't confuse reconciliation.
let _rowKeySeq = 0;
const withKey = (row) => ({ ...row, _key: row._key ?? ++_rowKeySeq });

function FinancialPlanSetup() {
  const [personalBills, setPersonalBills] = useState([withKey({ name: '', amount: 0 })]);
  const [businessExpenses, setBusinessExpenses] = useState([withKey({ name: '', amount: 0 })]);
  const [selectedTemplate, setSelectedTemplate] = useState('current');
  const [customAllocation, setCustomAllocation] = useState({
    taxes: 20,
    business: 20,
    personal: 55,
    savings: 5,
    custom_allocations: [],
    floor_enabled: false,
    floor_amount: 0,
    floor_target_account: 'personal',
    cap_enabled: false,
    cap_amount: 0
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [calculations, setCalculations] = useState(null);

  useEffect(() => {
    loadFinancialPlan();
  }, []);

  const handleSelectTemplate = (templateKey) => {
    setSelectedTemplate(templateKey);
    if (templateKey !== 'custom') {
      const templateAlloc = { ...ALLOCATION_TEMPLATES[templateKey].allocation, custom_allocations: [] };
      setCustomAllocation(templateAlloc);
    }
  };
  
  const handleCustomAllocationsUpdate = (newCustomAllocations) => {
    setCustomAllocation(prev => ({
      ...prev,
      custom_allocations: newCustomAllocations
    }));
  };

  // Live-computed allocation totals for the inline overage guard. Mirrors the
  // savePlan() math + backend tolerance so what the user sees on-screen is the
  // exact same rule that will run when they click Save.
  const primaryAllocationTotal = (customAllocation.taxes || 0)
    + (customAllocation.business || 0)
    + (customAllocation.personal || 0)
    + (customAllocation.savings || 0);
  const customAllocationTotal = (customAllocation.custom_allocations || [])
    .reduce((sum, alloc) => sum + (alloc.percentage || 0), 0);
  const allocationTotal = primaryAllocationTotal + customAllocationTotal;
  const isAllocationOver = allocationTotal > 100 + ALLOCATION_TOLERANCE;

  const loadFinancialPlan = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/financial-plan`);
      if (response.data) {
        setPersonalBills(response.data.personal_bills.length > 0 ? response.data.personal_bills.map(withKey) : [withKey({ name: '', amount: 0 })]);
        setBusinessExpenses(response.data.business_expenses.length > 0 ? response.data.business_expenses.map(withKey) : [withKey({ name: '', amount: 0 })]);
        
        // Load allocation if exists
        if (response.data.allocation) {
          setCustomAllocation(response.data.allocation);
          // Prefer the template_key the user explicitly saved. This preserves
          // "Custom" selections even when the percentages happen to match a
          // preset (e.g. someone switches to Custom but keeps 25/25/40/10).
          const savedKey = response.data.template_key;
          if (savedKey && (savedKey === 'custom' || ALLOCATION_TEMPLATES[savedKey])) {
            setSelectedTemplate(savedKey);
          } else {
            // Back-compat for plans saved before template_key existed —
            // fall back to matching percentages, defaulting to 'custom' if
            // custom allocations are present.
            const hasCustoms = (response.data.allocation.custom_allocations || []).length > 0;
            if (hasCustoms) {
              setSelectedTemplate('custom');
            } else {
              const matchingTemplate = Object.entries(ALLOCATION_TEMPLATES).find(([key, template]) =>
                template.allocation.taxes === response.data.allocation.taxes &&
                template.allocation.business === response.data.allocation.business &&
                template.allocation.personal === response.data.allocation.personal &&
                template.allocation.savings === response.data.allocation.savings
              );
              setSelectedTemplate(matchingTemplate ? matchingTemplate[0] : 'custom');
            }
          }
        }
        
        calculatePreview(response.data.personal_bills, response.data.business_expenses, response.data.allocation);
      }
    } catch (err) {
      console.error('Error loading financial plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculatePreview = (personal, business, allocation = null) => {
    const totalPersonal = personal.reduce((sum, bill) => sum + (parseFloat(bill.amount) || 0), 0);
    const totalBusiness = business.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
    
    const weeklyPersonal = totalPersonal / 4.33;
    const weeklyBusiness = totalBusiness / 4.33;
    
    // Use custom allocation if provided, otherwise use current selection
    const alloc = allocation || customAllocation;
    const personalPct = (alloc.personal || 0) / 100;
    // Business requirement uses the business allocation % only (same formula
    // the backend persists in `recommended_weekly_target`). Mixing in savings%
    // here caused the Plan's preview "True Minimum" to differ from what the
    // Dashboard later showed for the same plan.
    const businessPct = (alloc.business || 0) / 100;

    const minWeeklyPersonal = personalPct > 0 ? weeklyPersonal / personalPct : 0;
    const minWeeklyBusiness = businessPct > 0 ? weeklyBusiness / businessPct : 0;
    const recommendedTarget = Math.max(minWeeklyPersonal, minWeeklyBusiness);
    
    setCalculations({
      totalPersonal,
      totalBusiness,
      weeklyPersonal,
      weeklyBusiness,
      minWeeklyPersonal,
      minWeeklyBusiness,
      recommendedTarget,
      allocation: alloc
    });
  };

  const addPersonalBill = () => {
    setPersonalBills([...personalBills, withKey({ name: '', amount: 0 })]);
  };

  const removePersonalBill = (index) => {
    const updated = personalBills.filter((_, i) => i !== index);
    setPersonalBills(updated.length > 0 ? updated : [withKey({ name: '', amount: 0 })]);
    calculatePreview(updated, businessExpenses);
  };

  const updatePersonalBill = (index, field, value) => {
    const updated = [...personalBills];
    updated[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setPersonalBills(updated);
    calculatePreview(updated, businessExpenses);
  };

  const addBusinessExpense = () => {
    setBusinessExpenses([...businessExpenses, withKey({ name: '', amount: 0 })]);
  };

  const removeBusinessExpense = (index) => {
    const updated = businessExpenses.filter((_, i) => i !== index);
    setBusinessExpenses(updated.length > 0 ? updated : [withKey({ name: '', amount: 0 })]);
    calculatePreview(personalBills, updated);
  };

  const updateBusinessExpense = (index, field, value) => {
    const updated = [...businessExpenses];
    updated[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setBusinessExpenses(updated);
    calculatePreview(personalBills, updated);
  };

  const savePlan = async () => {
    // Validate allocation totals exactly 100% including custom allocations.
    // Overage tolerance mirrors the backend guard (0.005).
    const customTotal = (customAllocation.custom_allocations || []).reduce((sum, alloc) => sum + (alloc.percentage || 0), 0);
    const primaryTotal = (customAllocation.taxes || 0) + (customAllocation.business || 0) + (customAllocation.personal || 0) + (customAllocation.savings || 0);
    const total = primaryTotal + customTotal;

    if (total > 100 + ALLOCATION_TOLERANCE) {
      toast.error('Allocations exceed 100%. Reduce one or more buckets before saving.');
      return;
    }
    if (total < 100 - ALLOCATION_TOLERANCE) {
      toast.error(`Allocations must add up to 100%. Current total: ${total.toFixed(2)}%.`);
      return;
    }
    
    // Validate custom allocation names
    const customAllocs = customAllocation.custom_allocations || [];
    const hasEmptyNames = customAllocs.some(alloc => !alloc.name || alloc.name.trim() === '');
    if (hasEmptyNames) {
      toast.error('Please provide names for all custom allocations or remove empty ones.');
      return;
    }
    
    try {
      setSaving(true);
      setSuccess(false);
      beginSync('Saving plan');

      // Strip the client-side `_key` before persisting — it's a React
      // reconciliation helper, not plan data.
      const filteredPersonal = personalBills
        .filter(b => b.name && b.amount > 0)
        .map(({ _key, ...rest }) => rest); // eslint-disable-line no-unused-vars
      const filteredBusiness = businessExpenses
        .filter(e => e.name && e.amount > 0)
        .map(({ _key, ...rest }) => rest); // eslint-disable-line no-unused-vars
      
      await axios.post(`${API}/financial-plan`, {
        personal_bills: filteredPersonal,
        business_expenses: filteredBusiness,
        allocation: customAllocation,
        template_key: selectedTemplate
      });

      // Plan change affects how future allocations are split + monthly checks
      // recompute their bucket share → refresh every view reading balances.
      invalidateAccountBalances();
      endSync({ success: true });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      endSync({ success: false });
      console.error('Error saving financial plan:', err);
      // Backend now returns a structured detail object for the allocation
      // guard: `{error, message, ...}`. Fall back to plain string / generic.
      const detail = err.response?.data?.detail;
      const message = (detail && typeof detail === 'object' && detail.message)
        || (typeof detail === 'string' && detail)
        || 'Failed to save financial plan';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="setup-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financial-plan-setup">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold mb-2">Financial Plan Setup</h1>
        <p className="text-blue-100">STEP 1: Set up your monthly bills and business expenses (one-time setup)</p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4 flex items-center space-x-3" data-testid="success-message">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          <span className="text-green-800 dark:text-green-300 font-medium">Financial plan saved successfully!</span>
        </div>
      )}

      {/* Money flow explainer — plan-vs-behaviour framing */}
      <MoneyFlowExplainer testId="money-flow-plan-setup" />

      {/* Allocation Templates */}
      <div
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 ${
          isAllocationOver
            ? 'ring-2 ring-rose-500 dark:ring-rose-400 border border-rose-300 dark:border-rose-700'
            : ''
        }`}
        data-testid="allocation-strategy-section"
        data-allocation-over={isAllocationOver ? 'true' : 'false'}
      >
        {isAllocationOver && (
          <div
            className="mb-4 p-4 rounded-lg bg-rose-50 dark:bg-rose-900/30 border border-rose-300 dark:border-rose-700 flex items-start space-x-3"
            role="alert"
            data-testid="allocation-overage-banner"
          >
            <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 space-y-1">
              <p className="text-sm font-semibold text-rose-900 dark:text-rose-200">
                Allocations exceed 100%. Reduce one or more buckets before saving.
              </p>
              <p className="text-xs text-rose-800 dark:text-rose-300">
                Primary allocation: <strong>{primaryAllocationTotal.toFixed(2)}%</strong>
                {customAllocationTotal > 0 && (
                  <> + Custom allocations: <strong>{customAllocationTotal.toFixed(2)}%</strong></>
                )}
                {' '}= <strong>{allocationTotal.toFixed(2)}%</strong> (over by <strong>{(allocationTotal - 100).toFixed(2)}%</strong>).
              </p>
            </div>
          </div>
        )}
        <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-900/40 border-l-4 border-slate-400 dark:border-slate-600 rounded">
          <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="allocation-strategy-clarity">
            Your percentages are your <strong>operating targets</strong>. Expenses and actual balances may differ — which is why <strong>Buffer</strong> and <strong>Rebalance</strong> exist.
          </p>
        </div>
        <AllocationTemplates
          selectedTemplate={selectedTemplate}
          onSelectTemplate={handleSelectTemplate}
          customAllocation={customAllocation}
          onCustomChange={setCustomAllocation}
        />
        
        {/* Custom Allocations Manager - Only show when "Custom" template is selected */}
        {selectedTemplate === 'custom' && (
          <CustomAllocationManager
            customAllocations={customAllocation.custom_allocations || []}
            onUpdate={handleCustomAllocationsUpdate}
            baseAllocation={{
              taxes: customAllocation.taxes,
              business: customAllocation.business,
              personal: customAllocation.personal,
              savings: customAllocation.savings
            }}
          />
        )}
        
        {/* Floor Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Personal Floor</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">The minimum amount you want protected for personal needs before aggressive business growth or savings.</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5"><strong>Heads up:</strong> If this floor is too high, business funding may feel tight on lower-income weeks.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={customAllocation.floor_enabled || false}
                onChange={(e) => setCustomAllocation(prev => ({
                  ...prev,
                  floor_enabled: e.target.checked
                }))}
                className="sr-only peer"
                data-testid="floor-enabled-toggle"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          {customAllocation.floor_enabled && (
            <div className="space-y-4 mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Floor Amount (per paycheck)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={customAllocation.floor_amount || ''}
                    onChange={(e) => setCustomAllocation(prev => ({
                      ...prev,
                      floor_amount: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="e.g., 2000"
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    data-testid="floor-amount-input"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This amount is secured before any allocation percentages are applied.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Send Floor To
                </label>
                <select
                  value={customAllocation.floor_target_account || 'personal'}
                  onChange={(e) => setCustomAllocation(prev => ({
                    ...prev,
                    floor_target_account: e.target.value
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  data-testid="floor-target-select"
                >
                  <option value="personal">Personal Account</option>
                  <option value="business">Business Account</option>
                  <option value="taxes">Taxes Account</option>
                  <option value="savings">Savings Account</option>
                  {(customAllocation.custom_allocations || []).map((alloc) => (
                    <option key={`floor-opt-${alloc.name}`} value={alloc.name}>{alloc.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Example:</strong> If your floor is $2,000 and you earn $3,500:
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 mt-1 list-disc list-inside">
                  <li>First $2,000 → {customAllocation.floor_target_account === 'personal' ? 'Personal' : customAllocation.floor_target_account === 'business' ? 'Business' : customAllocation.floor_target_account === 'taxes' ? 'Taxes' : customAllocation.floor_target_account === 'savings' ? 'Savings' : customAllocation.floor_target_account}</li>
                  <li>Remaining $1,500 → Split by your allocation</li>
                </ul>
              </div>
            </div>
          )}
        </div>
        
        {/* Business Account Cap */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Business Cap</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">The maximum amount you want sitting in Business before excess can move into Buffer, Savings, or other priorities.</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5"><strong>Heads up:</strong> For high-burn businesses, this cap should be high enough to cover upcoming lead spend and operating costs.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={customAllocation.cap_enabled || false}
                onChange={(e) => setCustomAllocation(prev => ({
                  ...prev,
                  cap_enabled: e.target.checked
                }))}
                className="sr-only peer"
                data-testid="cap-enabled-toggle"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
            </label>
          </div>
          
          {customAllocation.cap_enabled && (
            <div className="space-y-4 mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Maximum Business Account Balance
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={customAllocation.cap_amount || ''}
                    onChange={(e) => setCustomAllocation(prev => ({
                      ...prev,
                      cap_amount: parseFloat(e.target.value) || 0
                    }))}
                    placeholder="e.g., 16500"
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    data-testid="cap-amount-input"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  When your business account exceeds this amount, excess funds are redistributed across your system.
                </p>
              </div>
              
              <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Why set a cap?</strong>
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 mt-1 list-disc list-inside">
                  <li>Prevents cash from sitting idle</li>
                  <li>Forces consistent distribution</li>
                  <li>Keeps your allocation system active</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Bills */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white dark:text-white">Fixed Personal Monthly Bills</h2>
            <button
              onClick={addPersonalBill}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              data-testid="add-personal-bill-btn"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Add</span>
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Your fixed monthly personal expenses.</p>
          <div className="space-y-3">
            {personalBills.map((bill, index) => (
              <div key={bill._key ?? `personal-${index}`} className="flex items-center space-x-2">
                <input
                  type="text"
                  placeholder="Bill name (e.g., Rent)"
                  value={bill.name}
                  onChange={(e) => updatePersonalBill(index, 'name', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
                  data-testid={`personal-bill-name-${index}`}
                />
                <input
                  type="number"
                  placeholder="Amount"
                  value={bill.amount || ''}
                  onChange={(e) => updatePersonalBill(index, 'amount', e.target.value)}
                  className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
                  data-testid={`personal-bill-amount-${index}`}
                />
                <button
                  onClick={() => removePersonalBill(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid={`remove-personal-bill-${index}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
          {calculations && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100">Total Monthly: <span className="text-blue-700 dark:text-blue-400 font-bold">${calculations.totalPersonal.toFixed(2)}</span></p>
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100">Weekly Required: <span className="text-blue-700 dark:text-blue-400 font-bold">${calculations.weeklyPersonal.toFixed(2)}</span></p>
            </div>
          )}
        </div>

        {/* Business Expenses */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white dark:text-white">Business Monthly Burn</h2>
            <button
              onClick={addBusinessExpense}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              data-testid="add-business-expense-btn"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Add</span>
            </button>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">Your average monthly business operating costs — leads, ads, CRM, software, assistants, office, payroll, and other business expenses.</p>
          <p className="text-xs text-amber-700 dark:text-amber-400 mb-4"><strong>Important:</strong> This should reflect your real average business spend, not just fixed subscriptions.</p>
          <div className="space-y-3">
            {businessExpenses.map((expense, index) => (
              <div key={expense._key ?? `business-${index}`} className="flex items-center space-x-2">
                <input
                  type="text"
                  placeholder="Expense name (e.g., CRM)"
                  value={expense.name}
                  onChange={(e) => updateBusinessExpense(index, 'name', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
                  data-testid={`business-expense-name-${index}`}
                />
                <input
                  type="number"
                  placeholder="Amount"
                  value={expense.amount || ''}
                  onChange={(e) => updateBusinessExpense(index, 'amount', e.target.value)}
                  className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
                  data-testid={`business-expense-amount-${index}`}
                />
                <button
                  onClick={() => removeBusinessExpense(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid={`remove-business-expense-${index}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
          {calculations && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100">Total Monthly: <span className="text-blue-700 dark:text-blue-400 font-bold">${calculations.totalBusiness.toFixed(2)}</span></p>
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100">Weekly Required: <span className="text-blue-700 dark:text-blue-400 font-bold">${calculations.weeklyBusiness.toFixed(2)}</span></p>
            </div>
          )}
        </div>
      </div>

      {/* Calculations Summary */}
      {calculations && (() => {
        // Safe, dynamic percentage normalisation — falls back to 0 if a
        // plan field is missing so we never render undefined%/NaN%.
        const pct = (v) => {
          const n = Number(v);
          return Number.isFinite(n) ? Math.round(n * 10) / 10 : 0;
        };
        const taxesPct = pct(calculations.allocation.taxes);
        const businessPct = pct(calculations.allocation.business);
        const personalPct = pct(calculations.allocation.personal);
        const savingsPct = pct(calculations.allocation.savings);
        const customs = calculations.allocation.custom_allocations || [];
        const customPctTotal = pct(
          customs.reduce((s, ca) => s + (Number(ca.percentage) || 0), 0)
        );
        // Buffer % is whatever is left over after the core 4 buckets + customs,
        // clamped to [0, 100] so an over-allocated plan never renders negative.
        const bufferPct = pct(
          Math.max(0, 100 - taxesPct - businessPct - personalPct - savingsPct - customPctTotal)
        );
        return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Your Required Weekly Income</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Based on your expenses and allocation plan. The <strong>True Minimum Income</strong> card is the canonical weekly number —
            it&apos;s stored as <code className="text-xs px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700">recommended_weekly_target</code>
            and drives the Dashboard <strong>Weekly Target → True Minimum</strong> headline.
          </p>

          {/* "This is your number" banner */}
          <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500 rounded">
            <p className="text-sm text-gray-800 dark:text-gray-100">
              💡 <strong>This is your number.</strong> If you hit it every week, every account (Personal, Business, Taxes, Savings{calculations.allocation.custom_allocations?.length > 0 ? ', plus your custom buckets' : ''}) stays fully funded.
            </p>
          </div>

          {/* Explanation Box */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded">
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              <strong>💰 How the math works:</strong> Each dollar you earn is split by your plan — {taxesPct}% Taxes, {businessPct}% Business, {personalPct}% Personal, {savingsPct}% Savings
              {customs.length > 0 && (
                <span>, plus {customs.map(ca => `${pct(ca.percentage)}% ${ca.name}`).join(', ')}</span>
              )}
              {bufferPct > 0 && <span>, with {bufferPct}% held as Buffer</span>}.
              So <strong>{personalPct}%</strong> of every dollar lands in your Personal account and
              <strong> {businessPct}%</strong> lands in your Business account.
              {customPctTotal > 0 && (
                <span> Custom allocations make up {customPctTotal}% of your plan.</span>
              )}
              {' '}Your weekly expenses are <strong>${calculations.weeklyPersonal.toFixed(2)}</strong> (personal) and
              <strong> ${calculations.weeklyBusiness.toFixed(2)}</strong> (business) — so the income needed to cover each bucket
              is the expense divided by the bucket&apos;s plan %.
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 leading-relaxed">
              <strong>Buffer</strong> is separate from this required-income math. It captures any unallocated capital
              retained outside your core plan buckets at time of entry (e.g. when you earn above your required minimum).
              Buffer is <em>not</em> required for minimum system funding; it&apos;s a real bucket you can redirect into
              Savings or another account via Rebalance whenever you choose.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-2 border-purple-200 dark:border-purple-700">
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">Personal Requirement</p>
              <p className="text-xs text-gray-700 dark:text-gray-300 mb-2">
                Weekly income needed so your <strong>{calculations.allocation.personal}%</strong> Personal allocation covers
                your <strong>${calculations.weeklyPersonal.toFixed(2)}</strong> weekly personal bills.
              </p>
              <p className="text-[11px] font-mono text-purple-900 dark:text-purple-300 mb-2">
                ${calculations.weeklyPersonal.toFixed(2)} ÷ {(calculations.allocation.personal / 100).toFixed(2)} = ${calculations.minWeeklyPersonal.toFixed(2)}
              </p>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-400" data-testid="min-income-personal">
                ${calculations.minWeeklyPersonal.toFixed(2)}
              </p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-2 border-blue-200 dark:border-blue-700">
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">Business Requirement</p>
              <p className="text-xs text-gray-700 dark:text-gray-300 mb-2">
                Weekly income needed so your <strong>{calculations.allocation.business}%</strong> Business allocation covers
                your <strong>${calculations.weeklyBusiness.toFixed(2)}</strong> weekly business expenses.
              </p>
              <p className="text-[11px] font-mono text-blue-900 dark:text-blue-300 mb-2">
                ${calculations.weeklyBusiness.toFixed(2)} ÷ {(calculations.allocation.business / 100).toFixed(2)} = ${calculations.minWeeklyBusiness.toFixed(2)}
              </p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-400" data-testid="min-income-business">
                ${calculations.minWeeklyBusiness.toFixed(2)}
              </p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-200 dark:border-green-700">
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">True Minimum Income</p>
              <p className="text-xs text-gray-700 dark:text-gray-300 mb-2">
                The greater of the Personal and Business requirements — because hitting one but missing the other
                still leaves a bucket under-funded.
              </p>
              <p className="text-[11px] font-mono text-green-900 dark:text-green-300 mb-2">
                max(${calculations.minWeeklyPersonal.toFixed(2)}, ${calculations.minWeeklyBusiness.toFixed(2)}) = ${calculations.recommendedTarget.toFixed(2)}
              </p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-400" data-testid="recommended-target">
                ${calculations.recommendedTarget.toFixed(2)}
              </p>
              <p className="text-[11px] text-green-800 dark:text-green-300 mt-2">
                Matches <strong>Dashboard → Weekly Target → True Minimum</strong> exactly. This is the number persisted as your plan&apos;s <em>recommended weekly target</em>.
              </p>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Save Button */}
      <div className="flex flex-col items-center">
        <button
          onClick={savePlan}
          disabled={saving || isAllocationOver}
          className="flex items-center space-x-3 px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-lg font-semibold shadow-lg"
          data-testid="save-plan-btn"
        >
          {saving ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <Save className="w-6 h-6" />
          )}
          <span>{saving ? 'Activating...' : 'Save & Activate Plan'}</span>
        </button>
        {isAllocationOver && (
          <p
            className="mt-3 text-xs text-rose-600 dark:text-rose-400 text-center max-w-md font-medium"
            data-testid="save-plan-blocked-hint"
          >
            Save is disabled — reduce your allocation totals to 100% first.
          </p>
        )}
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center max-w-md">
          This locks in your system and applies your allocation rules going forward.
        </p>
      </div>

      {/* How this works */}
      <HelpSection title="⚙️ How Your Plan Works" testId="help-plan-setup">
        <HelpP>This is where you design your financial system.</HelpP>
        <HelpRule />
        <HelpHeading>🧠 Your allocation determines:</HelpHeading>
        <HelpList
          items={[
            <>Your lifestyle (<strong>Personal %</strong>)</>,
            <>Your business growth (<strong>Business %</strong>)</>,
            <>Your tax protection (<strong>Taxes %</strong>)</>,
            <>Your wealth building (<strong>Savings %</strong>)</>,
          ]}
        />
        <HelpRule />
        <HelpHeading>🔄 Flow:</HelpHeading>
        <HelpP>All income goes into your Business Account first. From there, it is automatically split based on your percentages.</HelpP>
        <HelpRule />
        <HelpHeading>💡 Key controls:</HelpHeading>
        <HelpList
          items={[
            <><strong>Income Floor</strong> → Protects your personal lifestyle first</>,
            <><strong>Business Cap</strong> → Prevents excess money from sitting in business</>,
          ]}
        />
        <HelpCallout tone="emerald">
          🎯 <strong>Goal:</strong> Create a system that supports your life today while building for the future.
        </HelpCallout>
      </HelpSection>
    </div>
  );
}

export default FinancialPlanSetup;
